import {
  Modal,
  ScrollView,
  Text,
  View,
  Image,
  ActivityIndicator,
  Platform,
  TouchableOpacity,
} from "react-native";
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  memo,
  useRef,
} from "react";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { CommonButton } from "../../../../common/CommonButton";
import { LinearGradient } from "expo-linear-gradient";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import StreamView from "../../../../Components/ongoing-common-components/stream-components/StreamView";
import { AppScreens } from "../../../Utility/AppScreens";
import RtmpSetupTooltip from "../../../../Components/settings-common-components/RtmpSetupTooltip";
import GoToLiveEventPageTooltip from "../../../../Components/ongoing-common-components/stream-components/GoToLiveEventPageTooltip";
import { AppImages } from "../../../../utility/AppImages";
import Separator from "../../../../Components/Separator";
import { toast } from "react-toastify";
import { createStream, createTwitchStream } from "../../../../services/stream";
import {
  FetchTwitchUrlKey,
  FetchYouTubeUrlKey,
} from "../../../../services/eventsAPI";
import { useWsProvider } from "../../../../context/NativeApplicationContext";

// Import optimized types
import { EventDetails, UserData } from "./types/event.types";
import { safeAsync } from "./utils/eventUtils";
import { useCheckIn } from "./hooks/useCheckIn";
import { useCheckInTimer } from "./hooks/useEventTimer";
import { storeUserActivity, ACTIVITY_TYPES } from "../../../../services/activityService";

interface OnGoingGameShowStreamingProps {
  eventDetails: EventDetails | null;
  accessToken: string;
  username: string;
  IsEventCanceled: boolean;
  setDetails: (value?: any) => void;
  multiStreamEnabled?: boolean;
  isLessThanOneHour: boolean;
  participantLengthCheck: boolean;
  hasCheckedIn: boolean;
  twitchSwitch: boolean;
  userTwitchStreamConnected: boolean;
  twitchStreamCreated: boolean;
  youtubeSwitch: boolean;
  userYoutubeStreamConnected: boolean;
  youtubeStreamCreated: boolean;
  userData?: UserData | null;
  isOrganiser?: boolean;
}

interface SetupState {
  isEntering: boolean;
  enterStatus: string;
  isConnecting: boolean;
  isDownloading: boolean;
  downloadProgress: number;
  isDownloaded: boolean;
}

interface SelectedPlatforms {
  youtube: boolean;
  twitch: boolean;
}

const OnGoingGameShowStreaming = memo<OnGoingGameShowStreamingProps>(
  ({
    eventDetails,
    accessToken,
    username,
    IsEventCanceled,
    setDetails,
    multiStreamEnabled = false,
    isLessThanOneHour,
    participantLengthCheck,
    hasCheckedIn,
    twitchSwitch,
    userTwitchStreamConnected,
    twitchStreamCreated,
    youtubeSwitch,
    userYoutubeStreamConnected,
    youtubeStreamCreated,
    userData,
    isOrganiser = false,
  }) => {
    const styles = useStyles();
    const deviceSize = useDeviceSize();
    const navigation = useNavigation();
    const isMountedRef = useRef(true);

    // Check-in functionality
    const { canCheckIn, checkInTimeLeft } = useCheckInTimer(eventDetails?.eventStartTime);
    const {
      loading: checkInLoading,
      handleCheckIn,
      canUserCheckIn,
    } = useCheckIn(
      userData || null,
      eventDetails,
      isOrganiser,
      (updatedDetails) => {
        // Refresh event details after successful check-in
        if (setDetails) {
          setDetails();
        }
      }
    );

    // State management
    const [rtmpTooltip, setRtmpTooltip] = useState(false);
    const [os, setOs] = useState<"mac" | "windows" | "ios" | "android" | null>(
      null
    );
    const { isNativeAppConnected, setStreamConfig } = useWsProvider();

    const [setupState, setSetupState] = useState<SetupState>({
      isEntering: false,
      enterStatus: "",
      isConnecting: false,
      isDownloading: false,
      downloadProgress: 0,
      isDownloaded:
        Platform.OS === "web" &&
        !!localStorage.getItem("isKOBroadcasterDownloaded"),
    });

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Safe setup state handlers with mount check
    const updateSetupState = useCallback(
      (key: keyof SetupState, value: any) => {
        if (!isMountedRef.current) return;
        setSetupState((prev) => ({ ...prev, [key]: value }));
      },
      []
    );

    const safeSetDetails = useCallback(() => {
      if (!isMountedRef.current) return;
      setDetails();
    }, [setDetails]);

    // Improved download status check with time validation
    const checkDownloadStatus = useCallback(() => {
      if (Platform.OS === "web") {
        const downloaded = localStorage.getItem("isKOBroadcasterDownloaded");
        const lastDownloadTime = localStorage.getItem(
          "koBroadcasterDownloadTime"
        );

        if (downloaded && lastDownloadTime) {
          const downloadTime = new Date(lastDownloadTime).getTime();
          const now = new Date().getTime();
          const hoursSinceDownload = (now - downloadTime) / (1000 * 60 * 60);

          if (hoursSinceDownload < 24) {
            updateSetupState("isDownloaded", true);
            return true;
          } else {
            localStorage.removeItem("isKOBroadcasterDownloaded");
            localStorage.removeItem("koBroadcasterDownloadTime");
            updateSetupState("isDownloaded", false);
            return false;
          }
        }
        updateSetupState("isDownloaded", !!downloaded);
        return !!downloaded;
      }
      return false;
    }, [updateSetupState]);

    const [selectedPlatforms, setSelectedPlatforms] =
      useState<SelectedPlatforms>({
        youtube: youtubeSwitch,
        twitch: twitchSwitch,
      });

    const handleSetYoutubeSelected = useCallback((val: boolean) => {
      setSelectedPlatforms((prev) => ({ ...prev, youtube: val }));
    }, []);

    const handleSetTwitchSelected = useCallback((val: boolean) => {
      setSelectedPlatforms((prev) => ({ ...prev, twitch: val }));
    }, []);

    // Memoized computed values
    const competeWithoutStream = useMemo(
      () => eventDetails?.competitorSwitch ?? false,
      [eventDetails?.competitorSwitch]
    );

    const isOrganizer = useMemo(
      () => eventDetails?.organiserId === username,
      [eventDetails?.organiserId, username]
    );

    const memoizedEventDetails = useMemo(() => {
      if (!eventDetails) return null;

      return {
        eventID: eventDetails.eventID,
        streamingPlatforms: eventDetails.streamingPlatforms,
        organiserId: eventDetails.organiserId,
        organizerYoutubeStream: eventDetails.organizerYoutubeStream,
        challengerYoutubeStream: eventDetails.challengerYoutubeStream,
        organizerEmbedded: eventDetails.organizerEmbedded,
        challengerEmbedded: eventDetails.challengerEmbedded,
        eventStartTime: eventDetails.eventStartTime,
        userYoutubeStreamConnected: eventDetails.userYoutubeStreamConnected,
        userTwitchStreamConnected: eventDetails.userTwitchStreamConnected,
      };
    }, [eventDetails]);

    if (competeWithoutStream) {
      return (
        <View style={{ padding: 20 }}>
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_24,
              { color: AppColors.white, textAlign: "center", marginTop: 40 },
            ]}
          >
            You have chosen to compete without streaming. Submit your results
            after the competition.
          </Text>
        </View>
      );
    }

    useEffect(() => {
      if (Platform.OS === "web") {
        const p = (navigator.platform || "").toLowerCase();
        const ua = (navigator.userAgent || "").toLowerCase();
        if (p.includes("mac") || ua.includes("mac")) setOs("mac");
        else if (p.includes("win") || ua.includes("win")) setOs("windows");
        else setOs(null);
      } else {
        setOs(Platform.OS as "ios" | "android");
      }
    }, []);

    const macS3Link =
      "https://broadcaster.kallout.io/KO+Broadcaster-1.0.5-arm64.dmg";
    const windowsS3Link =
      "https://broadcaster.kallout.io/KO+Broadcaster+Setup+1.0.5.exe";
    const downloadLink =
      os === "mac" ? macS3Link : os === "windows" ? windowsS3Link : null;

    const noOfStreamingPlatformSelected =
      Number(selectedPlatforms.youtube) + Number(selectedPlatforms.twitch);
    const isStreamPlatformSetupCompleted = noOfStreamingPlatformSelected > 0;
    const isMultiStreamSelected = noOfStreamingPlatformSelected > 1;

    const completedSetupSteps =
      Number(isStreamPlatformSetupCompleted) + Number(isNativeAppConnected);
    const totalSetupSteps = 2;
    const streamSetupProgressPercentage =
      (completedSetupSteps / totalSetupSteps) * 100;
    const isStreamSetUpCompleted = streamSetupProgressPercentage === 100;

    const handleConnectToBroadcaster = useCallback(() => {
      if (Platform.OS !== "web") {
        toast.error(
          "Please use a desktop (macOS/Windows) to install the broadcaster."
        );
        return;
      }
      if (!downloadLink) {
        toast.error("Unsupported operating system");
        return;
      }

      updateSetupState("isConnecting", true);
      updateSetupState("isDownloading", true);
      updateSetupState("downloadProgress", 0);

      const xhr = new XMLHttpRequest();
      xhr.open("GET", downloadLink, true);
      xhr.responseType = "blob";

      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          updateSetupState(
            "downloadProgress",
            Math.round((event.loaded / event.total) * 100)
          );
        }
      };

      xhr.onload = () => {
        updateSetupState("isConnecting", false);
        updateSetupState("isDownloading", false);

        if (xhr.status === 200) {
          const blob = xhr.response;
          const fileName =
            os === "mac"
              ? "KO Broadcaster-1.0.5-arm64.dmg"
              : "KO Broadcaster Setup 1.0.5.exe";
          const linkElement = document.createElement("a");
          linkElement.href = URL.createObjectURL(blob);
          linkElement.download = fileName;
          document.body.appendChild(linkElement);
          linkElement.click();
          document.body.removeChild(linkElement);
          URL.revokeObjectURL(linkElement.href);

          localStorage.setItem("isKOBroadcasterDownloaded", "true");
          localStorage.setItem(
            "koBroadcasterDownloadTime",
            new Date().toISOString()
          );
          updateSetupState("isDownloaded", true);
          toast.success(
            "Download complete. Install and run the KO Broadcaster."
          );
        } else {
          toast.error("Failed to download the application.");
        }
      };

      xhr.onerror = () => {
        updateSetupState("isConnecting", false);
        updateSetupState("isDownloading", false);
        toast.error("Error occurred during download.");
      };

      xhr.send();
    }, [downloadLink, os, updateSetupState]);

    const handleCreateYoutubeStream = useCallback(async () => {
      if (
        youtubeStreamCreated ||
        !memoizedEventDetails?.eventID ||
        !accessToken
      ) {
        if (!memoizedEventDetails?.eventID || !accessToken) {
          toast.error("Missing event or auth details.");
        }
        return null;
      }

      try {
        const response = await safeAsync(() =>
          createStream(memoizedEventDetails.eventID, accessToken)
        );

        if (!isMountedRef.current || !response) return null;

        if (response.statusCode === 200) {
          safeSetDetails();
          return response;
        } else {
          // Check for specific error types in the response body
          const errorMessage =
            response.body?.message ||
            response.body?.error ||
            "Failed to create YouTube stream.";
          const error = new Error(errorMessage);
          // Pass along the error code for better handling
          if (response.body?.error === "YOUTUBE_LIVE_STREAMING_NOT_ENABLED") {
            error.message = "YOUTUBE_LIVE_STREAMING_NOT_ENABLED";
          }
          throw error;
        }
      } catch (error: any) {
        if (isMountedRef.current) {
          // Check for specific YouTube live streaming not enabled error
          if (
            error.message?.includes("YOUTUBE_LIVE_STREAMING_NOT_ENABLED") ||
            error.message?.includes("YouTube Live Streaming Not Enabled")
          ) {
            toast.info(
              "YouTube Live Streaming Not Enabled. Your YouTube account is not enabled for live streaming. Please request access to live streaming from YouTube. Once requested, it may take up to 24 hours to get approval.",
              { autoClose: 8000 }
            );
          } else {
            toast.error(`Failed to create YouTube stream: ${error.message}`);
          }
        }
        return null;
      }
    }, [
      memoizedEventDetails?.eventID,
      accessToken,
      youtubeStreamCreated,
      safeSetDetails,
      isOrganizer,
    ]);

    const handleCreateTwitchStream = useCallback(async () => {
      if (
        twitchStreamCreated ||
        !memoizedEventDetails?.eventID ||
        !accessToken
      ) {
        if (!memoizedEventDetails?.eventID || !accessToken) {
          toast.error("Missing event or auth details.");
        }
        return null;
      }

      try {
        const response = await safeAsync(() =>
          createTwitchStream(memoizedEventDetails.eventID, accessToken)
        );

        if (!isMountedRef.current || !response) return null;

        if (response.statusCode === 200) {
          safeSetDetails();
          return response;
        } else {
          throw new Error(
            response.body?.error || "Failed to create Twitch stream."
          );
        }
      } catch (error: any) {
        if (isMountedRef.current) {
          toast.error(`Failed to create Twitch stream: ${error.message}`);
        }
        return null;
      }
    }, [
      memoizedEventDetails?.eventID,
      accessToken,
      twitchStreamCreated,
      safeSetDetails,
      isOrganizer,
    ]);

    const handleCreateStream = useCallback(
      async (platform: string): Promise<boolean> => {
        try {
          updateSetupState("enterStatus", `Creating ${platform} stream...`);
          let response = null;
          if (platform === "YouTube") {
            if (youtubeStreamCreated) {
              updateSetupState(
                "enterStatus",
                `YouTube stream already created ✅`
              );
              return true;
            }
            response = await handleCreateYoutubeStream();
          } else if (platform === "Twitch") {
            if (twitchStreamCreated) {
              updateSetupState(
                "enterStatus",
                `Twitch stream already created ✅`
              );
              return true;
            }
            response = await handleCreateTwitchStream();
          }
          if (response?.statusCode === 200) {
            updateSetupState("enterStatus", `${platform} stream created ✅`);
            return true;
          } else {
            updateSetupState(
              "enterStatus",
              `Failed to create ${platform} stream ❌`
            );
            return false;
          }
        } catch (error) {
          updateSetupState(
            "enterStatus",
            `Failed to create ${platform} stream ❌`
          );
          return false;
        }
      },
      [
        handleCreateYoutubeStream,
        handleCreateTwitchStream,
        updateSetupState,
        youtubeStreamCreated,
        twitchStreamCreated,
      ]
    );

    const fetchTwitchURLAndStreamKey =
      useCallback(async (): Promise<boolean> => {
        if (!memoizedEventDetails?.eventID || !accessToken) {
          if (isMountedRef.current) {
            toast.error("Missing event or auth details.");
          }
          return false;
        }

        try {
          updateSetupState("enterStatus", "Fetching RTMP server for Twitch...");
          const twitchResponse = await safeAsync(() =>
            FetchTwitchUrlKey(accessToken, memoizedEventDetails.eventID)
          );

          if (!isMountedRef.current || !twitchResponse) return false;

          if (
            twitchResponse.statusCode === 200 ||
            twitchResponse.statusCode === 201
          ) {
            const rtmpURL = twitchResponse.body.RTMP_URL;
            const streamKey = twitchResponse.body.StreamKey;
            setStreamConfig({ twitchUrl: rtmpURL, twitchStreamKey: streamKey });
            updateSetupState("enterStatus", "Twitch stream key fetched ✅");
            return true;
          } else {
            throw new Error(
              twitchResponse.body?.error || "Failed to fetch Twitch stream key."
            );
          }
        } catch (error: any) {
          if (isMountedRef.current) {
            updateSetupState(
              "enterStatus",
              "Failed to fetch Twitch stream key ❌"
            );
            toast.error(`Error fetching Twitch stream key: ${error.message}`);
          }
          return false;
        }
      }, [
        accessToken,
        memoizedEventDetails?.eventID,
        setStreamConfig,
        updateSetupState,
      ]);

    const fetchYouTubeURLAndStreamKey =
      useCallback(async (): Promise<boolean> => {
        if (!memoizedEventDetails?.eventID || !accessToken) {
          if (isMountedRef.current) {
            toast.error("Missing event or auth details.");
          }
          return false;
        }

        try {
          updateSetupState(
            "enterStatus",
            "Fetching RTMP server for YouTube..."
          );
          const youTubeResponse = await safeAsync(() =>
            FetchYouTubeUrlKey(accessToken, memoizedEventDetails.eventID)
          );

          if (!isMountedRef.current || !youTubeResponse) return false;

          if (
            youTubeResponse.statusCode === 200 ||
            youTubeResponse.statusCode === 201
          ) {
            const rtmpURL = youTubeResponse.body.RTMP_URL;
            const streamKey = youTubeResponse.body.StreamKey;
            setStreamConfig({
              youtubeUrl: rtmpURL,
              youtubeStreamKey: streamKey,
            });
            updateSetupState("enterStatus", "YouTube stream key fetched ✅");
            return true;
          } else {
            throw new Error(
              youTubeResponse.body?.error ||
              "Failed to fetch YouTube stream key."
            );
          }
        } catch (error: any) {
          if (isMountedRef.current) {
            updateSetupState(
              "enterStatus",
              "Failed to fetch YouTube stream key ❌"
            );
            toast.error(`Error fetching YouTube stream key: ${error.message}`);
          }
          return false;
        }
      }, [
        accessToken,
        memoizedEventDetails?.eventID,
        setStreamConfig,
        updateSetupState,
      ]);

    const handleSetupStream = useCallback(async () => {
      if (setupState.isEntering) return;
      if (!isStreamPlatformSetupCompleted) {
        toast.error("Please select at least one streaming platform.");
        return;
      }
      if (!memoizedEventDetails.eventID || !accessToken) {
        toast.error("Missing event or auth details.");
        return;
      }

      updateSetupState("isEntering", true);
      updateSetupState("enterStatus", "Setting up stream...");
      let allStepsSuccessful = true;

      try {
        const selected: string[] = [];
        if (selectedPlatforms.youtube) selected.push("YouTube");
        if (selectedPlatforms.twitch) selected.push("Twitch");

        // Create streams
        for (const p of selected) {
          const createSuccess = await handleCreateStream(p);
          if (!createSuccess) {
            allStepsSuccessful = false;
          }
        }

        // Fetch keys only if creation succeeded
        if (allStepsSuccessful) {
          if (selectedPlatforms.youtube) {
            const ytKeySuccess = await fetchYouTubeURLAndStreamKey();
            if (!ytKeySuccess) {
              allStepsSuccessful = false;
            }
          }
          if (selectedPlatforms.twitch) {
            const twitchKeySuccess = await fetchTwitchURLAndStreamKey();
            if (!twitchKeySuccess) {
              allStepsSuccessful = false;
            }
          }
        }

        if (allStepsSuccessful) {
          updateSetupState("enterStatus", "Configuring KO Broadcaster...");
          updateSetupState("enterStatus", "Stream setup complete 🚀");

          // Track platform_connected activity for each platform
          const connectedPlatforms: string[] = [];
          if (selectedPlatforms.youtube) connectedPlatforms.push("YouTube");
          if (selectedPlatforms.twitch) connectedPlatforms.push("Twitch");
          storeUserActivity(accessToken, memoizedEventDetails.eventID, ACTIVITY_TYPES.PLATFORM_CONNECTED, {
            platforms: connectedPlatforms,
          })
            .then((result) => {
              if (result.success) {
                console.log("✅ Platform connected activity tracked:", result.activityId);
              }
            })
            .catch((err) => console.warn("⚠️ Error tracking platform connected:", err));

          // Track entered_studio activity
          storeUserActivity(accessToken, memoizedEventDetails.eventID, ACTIVITY_TYPES.ENTERED_STUDIO)
            .then((result) => {
              if (result.success) {
                console.log("✅ Entered studio activity tracked:", result.activityId);
              }
            })
            .catch((err) => console.warn("⚠️ Error tracking entered studio:", err));

          navigateToScreen(navigation, AppScreens.StreamSetup, {
            eventId: memoizedEventDetails.eventID,
          });
        } else {
          updateSetupState(
            "enterStatus",
            "Stream setup failed. Please try again."
          );
          toast.error(
            "Stream setup incomplete due to API errors. Please check your selections and try again."
          );
        }
      } catch (error) {
        allStepsSuccessful = false;
        toast.error("Error during stream setup.");
        updateSetupState(
          "enterStatus",
          "Stream setup failed. Please try again."
        );
      } finally {
        updateSetupState("isEntering", false);
      }
    }, [
      setupState.isEntering,
      isStreamPlatformSetupCompleted,
      memoizedEventDetails?.eventID,
      accessToken,
      selectedPlatforms,
      handleCreateStream,
      fetchYouTubeURLAndStreamKey,
      fetchTwitchURLAndStreamKey,
      navigation,
      updateSetupState,
    ]);

    return (
      <ScrollView showsHorizontalScrollIndicator={false}>
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <View
            style={{
              width:
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? "100%"
                  : "68%",
            }}
          >
            {deviceSize === DEVICE_SIZES.XS ||
              deviceSize === DEVICE_SIZES.SM ? (
              <>
                {/* Mobile: Setup Progress and Platform Mode - matching desktop layout */}
                <View style={{ flexDirection: "row", gap: 10, marginTop: 10, marginBottom: 10 }}>
                  {/* Setup Progress Card */}
                  <LinearGradient
                    colors={["#1F2937", "#111827"]}
                    start={[0, 0]}
                    end={[0, 1]}
                    style={{
                      padding: 8,
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 6,
                      flex: 1,
                      borderRadius: 8,
                    }}
                  >
                    <Text style={{ color: AppColors.white, fontSize: 14, fontWeight: "700", lineHeight: 16 }}>Setup Progress</Text>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                      {isStreamPlatformSetupCompleted ? (
                        <Image source={AppImages.icCompleteGreen} style={{ width: 16, height: 16 }} />
                      ) : (
                        <View style={{ width: 16, height: 16, borderRadius: 8, borderColor: "#6B7280", borderWidth: 2 }} />
                      )}
                      <Text style={{ color: isStreamPlatformSetupCompleted ? "white" : "#6B7280", fontSize: 10, fontWeight: "400", lineHeight: 12 }}>
                        Platform Connected ({noOfStreamingPlatformSelected})
                      </Text>
                    </View>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 8, width: "100%" }}>
                      {isNativeAppConnected ? (
                        <Image source={AppImages.icCompleteGreen} style={{ width: 16, height: 16 }} />
                      ) : (
                        <View style={{ width: 16, height: 16, borderRadius: 8, borderColor: "#6B7280", borderWidth: 2 }} />
                      )}
                      <Text
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        style={{ color: isNativeAppConnected ? "white" : "#6B7280", fontSize: 10, fontWeight: "400", lineHeight: 12, flex: 1 }}
                      >
                        KO Broadcaster Set up
                      </Text>
                    </View>
                    <View style={{ gap: 6, width: "100%" }}>
                      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                        <Text style={{ color: "#6B7280", fontSize: 13.5, fontWeight: "400", lineHeight: 20 }}>Setup Progress</Text>
                        <Text style={{ color: "white", fontSize: 13.5, fontWeight: "400", lineHeight: 20 }}>{completedSetupSteps}/{totalSetupSteps}</Text>
                      </View>
                      <View style={{ height: 6, backgroundColor: "#374151", borderRadius: 6 }}>
                        <View style={{ width: `${streamSetupProgressPercentage}%`, height: "100%", backgroundColor: "#3B82F6", borderRadius: 6 }} />
                      </View>
                    </View>
                  </LinearGradient>

                  {/* Platform Mode Card */}
                  <LinearGradient
                    colors={["#1F2937", "#111827"]}
                    start={[0, 0]}
                    end={[0, 1]}
                    style={{
                      padding: 8,
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 6,
                      flex: 1,
                      borderRadius: 8,
                    }}
                  >
                    <Text style={{ color: AppColors.white, fontSize: 14, fontWeight: "700", lineHeight: 16 }}>{isMultiStreamSelected ? "Multi" : "Single"} Platform Mode</Text>
                    <View style={{ flexDirection: "row", justifyContent: "space-between", width: "100%" }}>
                      <Text style={{ color: "white", fontSize: 10, fontWeight: "400", lineHeight: 12 }}>Platform:</Text>
                      <Text style={{ color: "white", fontSize: 10, fontWeight: "400", lineHeight: 12 }}>{noOfStreamingPlatformSelected}</Text>
                    </View>
                    <Separator color={"#4B5563"} height={1} marginTop={0} marginBottom={0} marginHorizontal={0} />
                    {selectedPlatforms.twitch && (
                      <View style={{ flexDirection: "row", gap: 5, alignItems: "center" }}>
                        <Image source={AppImages.icTwitchLogo} style={{ height: 18, width: 18 }} />
                        <Text style={{ color: "white", fontSize: 10, fontWeight: "400", lineHeight: 12 }}>Twitch ●</Text>
                      </View>
                    )}
                    {selectedPlatforms.youtube && (
                      <View style={{ flexDirection: "row", gap: 5, alignItems: "center" }}>
                        <Image source={AppImages.icYoutubeLogo} style={{ height: 18, width: 18 }} />
                        <Text style={{ color: "white", fontSize: 10, fontWeight: "400", lineHeight: 12 }}>YouTube ●</Text>
                      </View>
                    )}
                    {/* Complete Setup First Button */}
                    <TouchableOpacity
                      style={{
                        backgroundColor: isStreamPlatformSetupCompleted && isNativeAppConnected ? "#DC2626" : "#4B5563",
                        paddingVertical: 8,
                        paddingHorizontal: 12,
                        borderRadius: 8,
                        alignItems: "center",
                        width: "100%",
                        marginTop: "auto",
                      }}
                      disabled={!isStreamPlatformSetupCompleted || !isNativeAppConnected}
                    >
                      <Text style={{ color: "white", fontSize: 10, fontWeight: "600" }}>
                        {isStreamPlatformSetupCompleted && isNativeAppConnected ? "Ready to Stream" : "Complete Setup First"}
                      </Text>
                    </TouchableOpacity>
                  </LinearGradient>
                </View>
              </>
            ) : null}
            <StreamView
              styles={styles}
              eventDetails={memoizedEventDetails}
              accessToken={accessToken}
              username={username}
              setDetails={setDetails}
              isYoutubeSelected={selectedPlatforms.youtube}
              setIsYoutubeSelected={handleSetYoutubeSelected}
              isTwitchSelected={selectedPlatforms.twitch}
              setIsTwitchSelected={handleSetTwitchSelected}
              isStreamPlatformSetupCompleted={isStreamPlatformSetupCompleted}
              handleConnectToBroadcaster={handleConnectToBroadcaster}
              isConnecting={setupState.isConnecting}
              isDownloading={setupState.isDownloading}
              downloadProgress={setupState.downloadProgress}
              isDownloaded={setupState.isDownloaded}
            />
          </View>
          {deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM ? (
            <Modal
              visible={rtmpTooltip}
              transparent={true}
              animationType="fade"
              onRequestClose={() => setRtmpTooltip(false)}
            >
              <View
                style={{
                  flex: 1,
                  backgroundColor: AppColors.blackTrans_000000a8,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <TouchableOpacity
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                  }}
                  onPress={() => setRtmpTooltip(false)}
                />
                <RtmpSetupTooltip setRtmpTooltip={setRtmpTooltip} />
              </View>
            </Modal>
          ) : (
            <View style={{ width: "30%", marginTop: 10, marginBottom: 10 }}>
              {!rtmpTooltip ? (
                <View>
                  <LinearGradient
                    colors={["#1F2937", "#111827"]}
                    start={[0, 0]}
                    end={[0, 1]}
                    style={{
                      padding: 16,
                      paddingHorizontal: 24,
                      gap: 18,
                      borderRadius: 8,
                      width: "100%",
                    }}
                  >
                    <Text style={AppFontStyle.BOLD_ANYBODY_18}>
                      Setup Progress
                    </Text>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      {isStreamPlatformSetupCompleted ? (
                        <Image
                          source={AppImages.icCompleteGreen}
                          style={{ width: 18, height: 18 }}
                        />
                      ) : (
                        <View
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 9,
                            justifyContent: "center",
                            alignItems: "center",
                            borderColor: "#6B7280",
                            borderWidth: 2,
                          }}
                        />
                      )}
                      <Text
                        style={[
                          AppFontStyle.SEMIBOLD_ANYBODY_12,
                          {
                            color: isStreamPlatformSetupCompleted
                              ? "white"
                              : "#6B7280",
                          },
                        ]}
                      >
                        Platform Connected ({noOfStreamingPlatformSelected})
                      </Text>
                    </View>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      {isNativeAppConnected ? (
                        <Image
                          source={AppImages.icCompleteGreen}
                          style={{ width: 18, height: 18 }}
                        />
                      ) : (
                        <View
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 9,
                            justifyContent: "center",
                            alignItems: "center",
                            borderColor: "#6B7280",
                            borderWidth: 2,
                          }}
                        />
                      )}
                      <Text
                        style={[
                          AppFontStyle.SEMIBOLD_ANYBODY_12,
                          { color: isNativeAppConnected ? "white" : "#6B7280" },
                        ]}
                      >
                        KO Broadcaster Setup Complete
                      </Text>
                    </View>
                    <View style={{ gap: 12 }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text
                          style={[
                            AppFontStyle.SEMIBOLD_ANYBODY_12,
                            { color: "#6B7280" },
                          ]}
                        >
                          Setup Progress
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.SEMIBOLD_ANYBODY_12,
                            { color: "white" },
                          ]}
                        >
                          {completedSetupSteps}/{totalSetupSteps}
                        </Text>
                      </View>
                      <View
                        style={{
                          height: 8,
                          backgroundColor: "#374151",
                          borderRadius: 8,
                        }}
                      >
                        <View
                          style={{
                            width: `${streamSetupProgressPercentage}%`,
                            height: "100%",
                            backgroundColor: "#3B82F6",
                            borderRadius: 8,
                          }}
                        />
                      </View>
                    </View>
                  </LinearGradient>
                  <LinearGradient
                    colors={["#1F2937", "#111827"]}
                    start={[0, 0]}
                    end={[0, 1]}
                    style={{
                      padding: 16,
                      paddingHorizontal: 24,
                      gap: 18,
                      borderRadius: 8,
                      width: "100%",
                      marginTop: 20,
                    }}
                  >
                    <Text style={AppFontStyle.BOLD_ANYBODY_18}>
                      {isMultiStreamSelected ? "Multi" : "Single"} Platform Mode
                    </Text>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text
                        style={[
                          AppFontStyle.SEMIBOLD_ANYBODY_12,
                          { color: "white" },
                        ]}
                      >
                        Platform:
                      </Text>
                      <Text
                        style={[
                          AppFontStyle.SEMIBOLD_ANYBODY_12,
                          { color: "white" },
                        ]}
                      >
                        {noOfStreamingPlatformSelected}
                      </Text>
                    </View>
                    <Separator
                      color={"#4B5563"}
                      height={2}
                      marginTop={0}
                      marginBottom={0}
                      marginHorizontal={0}
                    />
                    {selectedPlatforms.twitch && (
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 5,
                        }}
                      >
                        <Image
                          source={AppImages.icTwitchLogo}
                          style={{ height: 20, width: 20 }}
                        />
                        <Text
                          style={[
                            AppFontStyle.SEMIBOLD_ANYBODY_12,
                            { color: "white", marginTop: 4 },
                          ]}
                        >
                          Twitch
                        </Text>
                      </View>
                    )}
                    {selectedPlatforms.youtube && (
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 5,
                        }}
                      >
                        <Image
                          source={AppImages.icYoutubeLogo}
                          style={{ height: 20, width: 20 }}
                        />
                        <Text
                          style={[
                            AppFontStyle.SEMIBOLD_ANYBODY_12,
                            { color: "white", marginTop: 4 },
                          ]}
                        >
                          YouTube
                        </Text>
                      </View>
                    )}
                  </LinearGradient>

                  {/* Buttons - Check in or Enter Stream studio */}
                  {!hasCheckedIn ? (
                    <View style={{ width: "100%", marginTop: 20 }}>
                      <TouchableOpacity
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          paddingVertical: 16,
                          paddingHorizontal: 63,
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "#4B5563",
                          borderRadius: 12,
                        }}
                        onPress={handleCheckIn}
                        disabled={!canUserCheckIn || !canCheckIn || checkInLoading}
                      >
                        {checkInLoading ? (
                          <>
                            <ActivityIndicator size="small" color="white" style={{ marginRight: 8 }} />
                            <Text style={{ color: "white" }}>Checking in...</Text>
                          </>
                        ) : (
                          <Text style={{ color: "white" }}>
                            Check in
                          </Text>
                        )}
                      </TouchableOpacity>
                      {!canCheckIn && (
                        <Text
                          style={{
                            color: "#9CA3AF",
                            fontSize: 11,
                            textAlign: "center",
                            marginTop: 4,
                          }}
                        >
                          {checkInTimeLeft && (checkInTimeLeft.days > 0 || checkInTimeLeft.hours > 0 || checkInTimeLeft.minutes > 0 || checkInTimeLeft.seconds > 0)
                            ? `Check-in opens in ${checkInTimeLeft.days > 0 ? `${checkInTimeLeft.days}d ` : ""
                              }${checkInTimeLeft.hours > 0 ? `${checkInTimeLeft.hours}h ` : ""
                              }${checkInTimeLeft.minutes > 0 ? `${checkInTimeLeft.minutes}m ` : ""
                              }${checkInTimeLeft.seconds > 0 ? `${checkInTimeLeft.seconds}s` : ""
                              }`.trim()
                            : "Check-in window not open yet"}
                        </Text>
                      )}
                    </View>
                  ) : isStreamSetUpCompleted && hasCheckedIn ? (
                    <LinearGradient
                      colors={["#DC2626", "#991B1B"]}
                      start={[0, 0]}
                      end={[1, 0]}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        paddingVertical: 16,
                        paddingHorizontal: 63,
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: 6,
                        marginTop: 20,
                      }}
                    >
                      <TouchableOpacity
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onPress={handleSetupStream}
                        disabled={setupState.isEntering}
                      >
                        {setupState.isEntering ? (
                          <>
                            <ActivityIndicator size="small" color="white" style={{ marginRight: 8 }} />
                            <Text style={{ color: "white" }}>
                              {setupState.enterStatus || "Setting up stream..."}
                            </Text>
                          </>
                        ) : (
                          <Text style={{ color: "white", fontWeight: "600" }}>
                            Enter Stream studio
                          </Text>
                        )}
                      </TouchableOpacity>
                    </LinearGradient>
                  ) : (
                    <TouchableOpacity
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        paddingVertical: 16,
                        paddingHorizontal: 63,
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#4B5563",
                        borderRadius: 12,
                        marginTop: 20,
                      }}
                      onPress={handleSetupStream}
                      disabled={true}
                    >
                      <Text style={{ color: "white" }}>
                        Enter Stream studio
                      </Text>
                    </TouchableOpacity>
                  )}

                  {/* Enable Store Past Broadcasts Section - Shown when Twitch is selected */}
                  {selectedPlatforms.twitch && (
                    <LinearGradient
                      colors={["#1F2937", "#111827"]}
                      start={[0, 0]}
                      end={[0, 1]}
                      style={{
                        padding: 16,
                        paddingHorizontal: 24,
                        borderRadius: 8,
                        width: "100%",
                        marginTop: 20,
                      }}
                    >
                      <Text
                        style={[
                          AppFontStyle.BOLD_ANYBODY_16,
                          { color: "white", marginBottom: 12 },
                        ]}
                      >
                        Enable 'Store Past Broadcasts'
                      </Text>

                      <Text
                        style={[
                          AppFontStyle.REGULAR_ANYBODY_14,
                          { color: AppColors.white, marginBottom: 16 },
                        ]}
                      >
                        To make sure your streams are saved automatically and can also
                        appear inside Kallout, you need to enable Twitch's 'Store Past
                        Broadcasts' option
                      </Text>

                      {/* VOD Settings Image */}
                      <Image
                        source={AppImages.Store_past_Stream_White}
                        style={{
                          width: 186,
                          height: 70,
                          marginBottom: 16,
                        }}
                        resizeMode="contain"
                      />

                      {/* Instructions */}
                      <View style={{ gap: 8, marginBottom: 16 }}>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: AppColors.white },
                          ]}
                        >
                          1. Open Twitch Settings
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: AppColors.white },
                          ]}
                        >
                          2. Go to Stream → VOD Settings
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { color: AppColors.white },
                          ]}
                        >
                          3. Turn ON Store Past Broadcasts
                        </Text>
                      </View>

                      {/* Open Settings Button */}
                      <CommonButton
                        title="Open Twitch settings"
                        onTap={() => {
                          if (Platform.OS === "web") {
                            window.open(
                              "https://dashboard.twitch.tv/settings/stream",
                              "_blank"
                            );
                          }
                        }}
                        buttonStyle={{
                          backgroundColor: AppColors.appViolentColor_6D08C3,
                          borderRadius: 8,
                          paddingVertical: 12,
                          paddingHorizontal: 20,
                          alignSelf: "flex-start",
                        }}
                        fontStyle={[
                          AppFontStyle.SEMIBOLD_ANYBODY_14,
                          { color: AppColors.white },
                        ]}
                      />
                    </LinearGradient>
                  )}
                </View>
              ) : (
                <RtmpSetupTooltip setRtmpTooltip={setRtmpTooltip} />
              )}
            </View>
          )}
        </View>
      </ScrollView>
    );
  }
);

export default memo(OnGoingGameShowStreaming);

const useStyles = CreateResponsiveStyle(
  {
    btnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 30,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      shadowOpacity: 0,
      height: 40,
      borderRadius: 10,
      marginVertical: 10,
      marginHorizontal: 10,
      alignSelf: "flex-start",
    },
    btnSetUpStreamStyle: {
      paddingVertical: 0,
      paddingHorizontal: 30,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      shadowOpacity: 0,
      height: 40,
      borderRadius: 10,
      marginVertical: 30,
      alignSelf: "flex-start",
    },
    streamView: {
      marginTop: 50,
      borderRadius: 5,
      borderWidth: 1.9,
      borderColor: AppColors.grayColor_282828,
      alignSelf: "flex-start",
      width: "50%",
    },
    deviceflexDirection: { flexDirection: "row" },
  },
  {
    [DEVICE_SIZES.XS]: {
      btnStyle: {
        paddingHorizontal: 10,
        marginVertical: 10,
        marginHorizontal: 0,
      },
      streamView: {
        marginTop: 50,
        borderRadius: 5,
        borderWidth: 1.9,
        borderColor: AppColors.grayColor_282828,
        alignSelf: "flex-start",
        width: "100%",
      },
      btnSetUpStreamStyle: {
        paddingVertical: 0,
        paddingHorizontal: 30,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        marginVertical: 30,
        alignSelf: "flex-start",
      },
      deviceflexDirection: { flexDirection: "row" },
    },
    [DEVICE_SIZES.SM]: {
      btnStyle: { paddingHorizontal: 10 },
      streamView: {
        marginTop: 50,
        borderRadius: 5,
        borderWidth: 1.9,
        borderColor: AppColors.grayColor_282828,
        alignSelf: "flex-start",
        width: "100%",
      },
      btnSetUpStreamStyle: {
        paddingVertical: 0,
        paddingHorizontal: 30,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        marginVertical: 30,
        alignSelf: "flex-start",
      },
      deviceflexDirection: { flexDirection: "row" },
    },
    [DEVICE_SIZES.MD]: {
      streamView: {
        marginTop: 50,
        borderRadius: 5,
        borderWidth: 1.9,
        borderColor: AppColors.grayColor_282828,
        alignSelf: "flex-start",
        width: "100%",
      },
      btnSetUpStreamStyle: {
        paddingVertical: 0,
        paddingHorizontal: 30,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        marginVertical: 30,
        alignSelf: "flex-start",
      },
      deviceflexDirection: { flexDirection: "row" },
    },
    [DEVICE_SIZES.LG]: {
      btnSetUpStreamStyle: {
        paddingVertical: 0,
        paddingHorizontal: 30,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        marginVertical: 30,
        alignSelf: "flex-start",
      },
      deviceflexDirection: { flexDirection: "row" },
    },
    [DEVICE_SIZES.XL]: {
      streamView: {
        marginTop: 50,
        borderRadius: 5,
        borderWidth: 1.9,
        borderColor: AppColors.grayColor_282828,
        alignSelf: "flex-start",
        width: "50%",
      },
      btnSetUpStreamStyle: {
        paddingVertical: 0,
        paddingHorizontal: 30,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        marginVertical: 30,
        alignSelf: "flex-start",
      },
      deviceflexDirection: { flexDirection: "row" },
    },
  }
);
